import LumaAccessiblePanel from '../admin/CoworkFormPanel';
import {Dialog as LumaAccessibleDialog, DialogTitle as LumaAccessibleTitle} from '@headlessui/react';
// DeleteCategoryModal.tsx — Co Marca — Eliminar Categoría
import React, { useState } from 'react';
import { createPortal } from 'react-dom';
import { Trash2, X, AlertTriangle } from 'lucide-react';
import { deleteCategory } from '../../services/categoryService';

interface DeleteCategoryModalProps {
    isOpen: boolean;
    onClose: () => void;
    category: { id: number; nombre: string } | null;
    onDeleted: () => void;
}

const colors = {
    primary:        'var(--ad-primary, #3564db)',
    primaryDark:    'var(--ad-primary-dark, #8f1f64)',
    secondary:      'var(--ad-secondary, #3564db)',
    secondaryLight: 'var(--ad-secondary, #92b1ed)',
    accentDark:     '#ea8406',
};

const css = `

.dm-modal * { font-family: 'DM Sans', sans-serif; box-sizing: border-box; }
@keyframes dmFadeIn  { from { opacity:0; } to { opacity:1; } }
@keyframes dmSlideUp { from { opacity:0; transform:translateY(20px) scale(.97); } to { opacity:1; transform:translateY(0) scale(1); } }
@keyframes dmShake   { 0%,100% { transform:translateX(0); } 20%,60% { transform:translateX(-4px); } 40%,80% { transform:translateX(4px); } }
@keyframes spin { to { transform:rotate(360deg); } }
.dm-overlay { animation: dmFadeIn  .2s ease both; }
.dm-dialog  { animation: dmSlideUp .3s cubic-bezier(.16,1,.3,1) both; }
.dm-btn-delete {
    display:inline-flex; align-items:center; gap:7px;
    background:linear-gradient(135deg,${colors.primary},${colors.primaryDark});
    color:#fff; border:none; border-radius:11px;
    padding:10px 22px; font-size:.88rem; font-weight:800;
    cursor:pointer; box-shadow:0 4px 14px rgba(var(--ad-primary-rgb, 193, 52, 139),.28);
    transition:all .22s cubic-bezier(.16,1,.3,1);
}
.dm-btn-delete:hover:not(:disabled) { transform:translateY(-1px); box-shadow:0 8px 22px rgba(var(--ad-primary-rgb, 193, 52, 139),.36); }
.dm-btn-delete:disabled { background:var(--ad-line, #e2e8f0); color:var(--ad-muted, #94a3b8); box-shadow:none; cursor:not-allowed; }
.dm-btn-cancel {
    display:inline-flex; align-items:center; gap:7px;
    background:var(--ad-soft, #f8fafc); border:1.5px solid var(--ad-line, #e2e8f0);
    color:var(--ad-ink, #374151); border-radius:11px;
    padding:10px 18px; font-size:.88rem; font-weight:700;
    cursor:pointer; transition:all .2s;
}
.dm-btn-cancel:hover { background:var(--ad-line, #f1f5f9); }
`;

const DeleteCategoryModal: React.FC<DeleteCategoryModalProps> = ({ isOpen, onClose, category, onDeleted }) => {
    const [isLoading, setIsLoading] = useState(false);

    const handleConfirm = async () => {
        if (!category) return;
        setIsLoading(true);
        try {
            await deleteCategory(category.id);
            onDeleted();
        } catch (error) {
            /* Browser diagnostics omitted. */
        } finally {
            setIsLoading(false);
            onClose();
        }
    };

    if (!isOpen) return null;

    return createPortal(
        <>
            <style>{css}</style>
            <LumaAccessibleDialog open={isOpen} onClose={onClose} data-luma-overlay="true" className="dm-modal" style={{ position:'fixed', inset:0, zIndex:99999, display:'flex', alignItems:'center', justifyContent:'center', padding:'16px' }}>
                {/* Overlay */}
                <div className="dm-overlay" onClick={onClose} style={{ position:'absolute', inset:0, background:'rgba(15,23,42,.6)', backdropFilter:'blur(6px)' }}/>

                {/* Dialog */}
                <LumaAccessiblePanel data-luma-dialog="true" className="dm-dialog card custom-card vx-card" style={{ position:'relative', background:'var(--ad-surface, #fff)', borderRadius:'24px', width:'100%', maxWidth:'440px', boxShadow:'0 32px 80px rgba(0,0,0,.20)', border:'1.5px solid var(--ad-line, #f1f5f9)', overflow:'hidden' }}>

                    {/* Barra top roja */}
                    <div style={{ height:'4px', background:`linear-gradient(90deg,${colors.primary},${colors.primaryDark},color-mix(in srgb, ${colors.primary} 50.2%, transparent))`, borderRadius:'24px 24px 0 0' }}/>

                    {/* Header */}
                    <div style={{ padding:'24px 24px 0', display:'flex', alignItems:'flex-start', justifyContent:'space-between' }}>
                        <div style={{ display:'flex', alignItems:'center', gap:'12px' }}>
                            {/* Icono de alerta */}
                            <div style={{ width:'44px', height:'44px', borderRadius:'14px', background:`color-mix(in srgb, ${colors.primary} 7.06%, transparent)`, border:`1.5px solid color-mix(in srgb, ${colors.primary} 13.33%, transparent)`, display:'flex', alignItems:'center', justifyContent:'center', flexShrink:0 }} className="">
                                <AlertTriangle size={20} color={colors.primary}/>
                            </div>
                            <div>
                                <LumaAccessibleTitle style={{ fontSize:'1.05rem', fontWeight:'650', color:'var(--ad-ink, #0f172a)', letterSpacing:'-.02em' }}>Confirmar eliminación</LumaAccessibleTitle>
                                <p style={{ fontSize:'.72rem', color:'var(--ad-muted, #94a3b8)', fontWeight:'500', marginTop:'2px' }}>Esta acción no se puede deshacer</p>
                            </div>
                        </div>
                        <button onClick={onClose} style={{ width:'30px', height:'30px', borderRadius:'9px', border:'1.5px solid var(--ad-line, #f1f5f9)', background:'var(--ad-soft, #f8fafc)', display:'flex', alignItems:'center', justifyContent:'center', cursor:'pointer', flexShrink:0 }}
                            onMouseEnter={e=>{ (e.currentTarget as HTMLButtonElement).style.background='var(--ad-line, #f1f5f9)'; }}
                            onMouseLeave={e=>{ (e.currentTarget as HTMLButtonElement).style.background='var(--ad-soft, #f8fafc)'; }}>
                            <X size={14} color="var(--ad-muted, #64748b)"/>
                        </button>
                    </div>

                    {/* Body */}
                    <div style={{ padding:'20px 24px 24px' }}>
                        {/* Barra de colores */}
                        <div style={{ display:'flex', gap:'4px', marginBottom:'20px' }}>
                            {[colors.primary, colors.secondary, colors.accentDark, colors.secondaryLight].map((c,i)=>(
                                <div key={i} style={{ height:'2px', width:i===0?'18px':'6px', borderRadius:'2px', background:c, opacity:.5 }}/>
                            ))}
                        </div>

                        {/* Mensaje */}
                        <p style={{ fontSize:'.9rem', color:'var(--ad-muted, #475569)', lineHeight:1.65, marginBottom:'16px' }}>
                            ¿Estás seguro que deseas eliminar la categoría{' '}
                            <strong style={{ color:'var(--ad-link, #2854c5)', fontWeight:'650' }}>"{category?.nombre}"</strong>?
                        </p>

                        {/* Card de advertencia */}
                        <div style={{ background:`color-mix(in srgb, ${colors.primary} 2.75%, transparent)`, border:`1.5px solid color-mix(in srgb, ${colors.primary} 9.41%, transparent)`, borderRadius:'12px', padding:'12px 14px', display:'flex', alignItems:'flex-start', gap:'10px', marginBottom:'24px' }} className="card custom-card vx-card">
                            <Trash2 size={14} color={colors.primary} style={{ flexShrink:0, marginTop:'1px' }}/>
                            <p style={{ fontSize:'.78rem', color:'var(--ad-link, #2854c5)', fontWeight:'600', lineHeight:1.5 }}>
                                Se eliminará permanentemente la categoría y todos los datos asociados a ella.
                            </p>
                        </div>

                        {/* Botones */}
                        <div style={{ display:'flex', justifyContent:'flex-end', gap:'10px' }}>
                            <button onClick={onClose} className="dm-btn-cancel">Cancelar</button>
                            <button onClick={handleConfirm} disabled={isLoading} className="dm-btn-delete">
                                {isLoading
                                    ? <><span style={{ width:'13px', height:'13px', border:'2px solid rgba(255,255,255,.4)', borderTopColor:'#fff', borderRadius:'50%', animation:'spin .7s linear infinite', display:'inline-block' }}/> Eliminando...</>
                                    : <><Trash2 size={14}/> Eliminar</>
                                }
                            </button>
                        </div>
                    </div>
                </LumaAccessiblePanel>
            </LumaAccessibleDialog>
        </>,
        document.body
    );
};

export default DeleteCategoryModal;
